import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import SEOHead from '@/components/SEOHead';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { supabase } from '@/lib/supabase';

const SITE_URL = import.meta.env.VITE_SITE_URL || 'https://example.com';

interface Category {
  id: number;
  name: string;
  slug: string;
  sort_order: number;
}

interface Album {
  id: number;
  category_id: number;
  title: string;
  description: string;
  cover_image: string;
  sort_order: number;
}

export default function Portfolio() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [categories, setCategories] = useState<Category[]>([]);
  const [albums, setAlbums] = useState<Album[]>([]);
  const [activeCategory, setActiveCategory] = useState<string>('');
  const [photoCounts, setPhotoCounts] = useState<Record<number, number>>();
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    async function fetchCategories() {
      const { data } = await supabase
        .from('portfolio_categories')
        .select('*')
        .order('sort_order');
      if (data && data.length > 0) {
        setCategories(data);
        // Se veio ?categoria=xxx pela URL, usa ela; senão usa a primeira
        const urlCategory = searchParams.get('categoria');
        if (urlCategory && data.some((c) => c.slug === urlCategory)) {
          setActiveCategory(urlCategory);
        } else {
          setActiveCategory(data[0].slug);
        }
      }
    }
    fetchCategories();
  }, [searchParams]);

  useEffect(() => {
    if (!activeCategory) return;

    async function fetchAlbums() {
      setLoading(true);
      const { data: catData } = await supabase
        .from('portfolio_categories')
        .select('id')
        .eq('slug', activeCategory)
        .maybeSingle();

      if (!catData) {
        setLoading(false);
        return;
      }

      const { data: albumData } = await supabase
        .from('portfolio_albums')
        .select('*')
        .eq('category_id', catData.id)
        .order('sort_order');

      if (albumData) {
        setAlbums(albumData);

        const albumIds = albumData.map((a) => a.id);
        const { data: photoData } = await supabase
          .from('portfolio_photos')
          .select('album_id')
          .in('album_id', albumIds);

        const counts: Record<number, number> = {};
        photoData?.forEach((p) => {
          counts[p.album_id] = (counts[p.album_id] || 0) + 1;
        });
        setPhotoCounts(counts);
      }
      setLoading(false);
    }
    fetchAlbums();
  }, [activeCategory]);

  const schema = [
    {
      '@type': 'CollectionPage',
      name: 'Portfólio - Jéssica Bonini Fotógrafa',
      description: 'Explore nossas histórias por categoria. Casamentos, pré-wedding, sessão família e eventos corporativos.',
      url: `${SITE_URL}/portfolio`,
      inLanguage: 'pt-BR',
    },
    {
      '@type': 'WebPage',
      name: 'Portfólio de Fotografia',
      description: 'Portfólio completo de fotografia profissional com álbuns de casamentos, pré-wedding, sessão família e eventos corporativos.',
      url: `${SITE_URL}/portfolio`,
    },
  ];

  return (
    <div className="min-h-screen">
      <SEOHead
        title="Portfólio | Jéssica Bonini Fotógrafa - Casamentos & Eventos"
        description="Explore nosso portfólio completo de fotografia profissional. Álbuns de casamentos, pré-wedding, sessão família e eventos corporativos em Sorocaba e região."
        keywords="portfólio fotografia, fotos casamento, álbum pré-wedding, sessão família fotos, eventos corporativos"
        canonicalPath="/portfolio"
        ogType="website"
        schema={schema}
      />
      <Navbar />
      <main>
        <section className="pt-32 md:pt-40 pb-16 md:pb-24 bg-cream">
          <div className="px-6 md:px-10 lg:px-16 text-center">
            <span className="text-xs font-semibold tracking-widest uppercase text-warm-600 mb-4 block">
              Portfólio
            </span>
            <h1 className="font-serif text-4xl md:text-5xl lg:text-6xl text-dark font-normal leading-tight mb-6">
              As <span className="italic">histórias</span>.🤎
            </h1>
            <p className="text-dark/60 text-base md:text-lg max-w-2xl mx-auto leading-relaxed">
              Explore nossas histórias por categoria. Cada pasta guarda memórias únicas e emoções reais.
            </p>
          </div>
        </section>

        <section className="py-16 md:py-24 bg-white">
          <div className="px-6 md:px-10 lg:px-16">
            <div className="flex flex-wrap justify-center gap-3 mb-12 md:mb-16">
              {categories.map((cat) => (
                <button
                  key={cat.slug}
                  onClick={() => setActiveCategory(cat.slug)}
                  className={`px-5 py-2 rounded-full text-sm font-medium transition-all duration-300 whitespace-nowrap ${
                    activeCategory === cat.slug
                      ? 'bg-dark text-white'
                      : 'bg-cream text-dark/70 hover:bg-warm-100'
                  }`}
                >
                  {cat.name}
                </button>
              ))}
            </div>

            {loading ? (
              <div className="flex justify-center py-20">
                <div className="w-10 h-10 border-2 border-dark/20 border-t-dark rounded-full animate-spin" />
              </div>
            ) : albums.length === 0 ? (
              <div className="text-center py-20">
                <p className="text-dark/50 text-lg">Nenhuma pasta encontrada nesta categoria.</p>
              </div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5 md:gap-6">
                {albums.map((album) => (
                  <div
                    key={album.id}
                    onClick={() => navigate(`/portfolio/${album.id}`)}
                    className="group relative aspect-[3/4] rounded-xl md:rounded-2xl overflow-hidden cursor-pointer bg-warm-100"
                  >
                    <img
                      src={album.cover_image}
                      alt={album.title}
                      className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent" />
                    <div className="absolute bottom-0 left-0 right-0 p-4 md:p-5">
                      <h3 className="text-white font-serif text-lg md:text-xl mb-1 leading-tight">
                        {album.title}
                      </h3>
                      <p className="text-white/70 text-xs md:text-sm line-clamp-2 mb-2">
                        {album.description}
                      </p>
                      <span className="inline-flex items-center gap-1.5 px-3 py-1 bg-white/20 backdrop-blur-sm rounded-full text-xs text-white">
                        <i className="ri-image-line" />
                        {photoCounts?.[album.id] || 0} fotos
                      </span>
                    </div>
                    <div className="absolute top-3 right-3 md:top-4 md:right-4 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                      <span className="w-10 h-10 flex items-center justify-center rounded-full bg-white/90 text-dark">
                        <i className="ri-arrow-right-line text-lg" />
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}